import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    ShieldAlert, Search, Filter, Download,
    ExternalLink, CheckCircle2, History,
    RefreshCw, Mail, Globe2, Lock, Eye, ChevronLeft,
    ChevronRight, TrendingUp, ShieldCheck, Bug, Radio,
    Clock, ArrowRight, X, Check, Hash, Play,
    AlertTriangle
} from "lucide-react";
import { backendURL } from "../config";

const SEVERITY_CONFIG = {
    critical: {
        bg: "bg-red-500/10 border-red-500/30 text-red-400",
        badge: "bg-red-500/20 text-red-400 border border-red-500/30",
        dot: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]",
        glow: "bg-red-500"
    },
    high: {
        bg: "bg-orange-500/10 border-orange-500/30 text-orange-400",
        badge: "bg-orange-500/20 text-orange-400 border border-orange-500/30",
        dot: "bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.8)]",
        glow: "bg-orange-500"
    },
    medium: {
        bg: "bg-amber-500/10 border-amber-500/30 text-amber-400",
        badge: "bg-amber-500/20 text-amber-400 border border-amber-500/30",
        dot: "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.8)]",
        glow: "bg-amber-500"
    },
    low: {
        bg: "bg-blue-500/10 border-blue-500/30 text-blue-400",
        badge: "bg-blue-500/20 text-blue-400 border border-blue-500/30",
        dot: "bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]",
        glow: "bg-blue-500"
    }
};

// Executive Stat Card
const MetricTile = ({ title, value, sub, icon: Icon, color = "blue", badge = null }) => {
    const colorMap = {
        blue: "bg-blue-500/10 border-blue-500/20 text-blue-400",
        red: "bg-red-500/10 border-red-500/20 text-red-400",
        orange: "bg-orange-500/10 border-orange-500/20 text-orange-400",
        purple: "bg-purple-500/10 border-purple-500/20 text-purple-400",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
    };

    return (
        <div className="bg-[#0F172A]/90 backdrop-blur-xl border border-white/[0.08] p-5 rounded-2xl relative overflow-hidden group shadow-xl transition-all duration-300 hover:border-white/[0.15]">
            <div className={`absolute top-0 right-0 w-28 h-28 ${colorMap[color].split(" ")[0]} blur-3xl -mr-12 -mt-12 transition-all duration-500 group-hover:opacity-80 opacity-30`} />

            <div className="flex items-center justify-between mb-3 relative z-10">
                <div className={`p-2.5 rounded-xl border ${colorMap[color]}`}>
                    <Icon className="w-5 h-5" />
                </div>
                {badge && (
                    <span className="text-[9px] font-black font-mono px-2 py-0.5 rounded-md bg-[#1E293B] border border-white/[0.08] text-slate-300 uppercase tracking-wider">
                        {badge}
                    </span>
                )}
            </div>

            <div className="relative z-10">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{title}</p>
                <div className="text-2xl lg:text-3xl font-black text-slate-100 font-mono tracking-tight leading-none mb-1.5">
                    {value}
                </div>
                <p className="text-[11px] text-slate-400 font-medium truncate">{sub}</p>
            </div>
        </div>
    );
};

export default function DarkWebMonitoring() {
    const navigate = useNavigate();

    // Core Data States
    const [alerts, setAlerts] = useState([]);
    const [stats, setStats] = useState({ totalAlerts: 0, highSeverity: 0, totalBreaches: 0 });
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalAlertsCount, setTotalAlertsCount] = useState(0);

    // Search & Filter States
    const [scanType, setScanType] = useState("email");
    const [scanInput, setScanInput] = useState("");
    const [isScanning, setIsScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [filters, setFilters] = useState({ severity: "", type: "", status: "" });
    const [activeSearch, setActiveSearch] = useState("");
    const [autoRefresh, setAutoRefresh] = useState(false);

    // Drilldown Inspection Modal
    const [inspectAlert, setInspectAlert] = useState(null);

    const token = localStorage.getItem("token");
    const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

    const fetchData = useCallback(async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        try {
            // 1. Fetch Intelligence Stats
            const intelRes = await axios.get(`${backendURL}/api/darkweb/intelligence`, { headers });
            if (intelRes.data) {
                setStats(intelRes.data.stats || { totalAlerts: 0, highSeverity: 0, totalBreaches: 0 });
            }

            // 2. Fetch Filtered Alerts
            const queryParams = new URLSearchParams({
                page: String(page),
                limit: "10",
                ...(filters.severity && { severity: filters.severity }),
                ...(filters.type && { type: filters.type }),
                ...(filters.status && { status: filters.status }),
                ...(activeSearch && { search: activeSearch })
            }).toString();

            const alertsRes = await axios.get(`${backendURL}/api/darkweb/alerts?${queryParams}`, { headers });
            if (alertsRes.data) {
                setAlerts(alertsRes.data.alerts || []);
                setTotalPages(alertsRes.data.pages || 1);
                setTotalAlertsCount(alertsRes.data.total || 0);
            }
        } catch (err) {
            console.error("Error fetching dark web data:", err);
        } finally {
            setLoading(false);
        }
    }, [headers, page, filters, activeSearch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Auto-refresh timer
    useEffect(() => {
        let interval = null;
        if (autoRefresh) {
            interval = setInterval(() => {
                fetchData(true);
            }, 30000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [autoRefresh, fetchData]);

    // Handle On-demand Scan
    const handleManualScan = async (e) => {
        e.preventDefault();
        if (!scanInput.trim()) return;

        setIsScanning(true);
        setScanResult(null);
        try {
            const res = await axios.post(`${backendURL}/api/darkweb/search`,
                { query: scanInput.trim(), type: scanType },
                { headers }
            );

            setScanResult(res.data);
            setActiveSearch(scanInput.trim());
            setPage(1);
            fetchData();
        } catch (err) {
            console.error("Scan error:", err);
            setScanResult({
                isClean: false,
                query: scanInput.trim(),
                message: "Deep web scan failed. Please verify target format and connectivity."
            });
        } finally {
            setIsScanning(false);
        }
    };

    // Update Alert Status
    const handleUpdateStatus = async (id, newStatus) => {
        try {
            await axios.post(`${backendURL}/api/darkweb/alerts/${id}/status`,
                { status: newStatus },
                { headers }
            );
            setAlerts(prev => prev.map(a => a._id === id ? { ...a, status: newStatus } : a));
            if (inspectAlert && inspectAlert._id === id) {
                setInspectAlert(prev => ({ ...prev, status: newStatus }));
            }
        } catch (err) {
            console.error("Failed to update status:", err);
        }
    };

    // Export CSV / JSON
    const exportData = (format) => {
        if (alerts.length === 0) return;

        if (format === "json") {
            const blob = new Blob([JSON.stringify(alerts, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `darkweb_threat_intel_${new Date().toISOString().split("T")[0]}.json`;
            a.click();
        } else {
            const headersList = ["Source", "Type", "Severity", "Title", "Affected Entity", "URL", "Date Found", "Status"];
            const rows = alerts.map(a => [
                `"${a.source}"`,
                `"${a.type}"`,
                `"${(a.severity || 'medium').toUpperCase()}"`,
                `"${(a.title || '').replace(/"/g, '""')}"`,
                `"${a.affectedEntity || ''}"`,
                `"${a.url || ''}"`,
                `"${a.dateFound || ''}"`,
                `"${a.status || 'new'}"`
            ]);

            const csvContent = "data:text/csv;charset=utf-8,"
                + headersList.join(",") + "\n"
                + rows.map(r => r.join(",")).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.href = encodedUri;
            link.download = `darkweb_threat_intel_${new Date().toISOString().split("T")[0]}.csv`;
            link.click();
        }
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return "Recent";
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
        } catch {
            return dateStr;
        }
    };

    const clearFilters = () => {
        setFilters({ severity: "", type: "", status: "" });
        setActiveSearch("");
        setScanInput("");
        setScanResult(null);
        setPage(1);
    };

    const activeFilterCount = (filters.severity ? 1 : 0) +
        (filters.type ? 1 : 0) +
        (filters.status ? 1 : 0) +
        (activeSearch ? 1 : 0);

    return (
        <div className="p-4 lg:p-8 space-y-6 max-w-[1700px] mx-auto min-h-screen text-slate-100 font-sans pb-24 animate-in fade-in duration-500">

            {/* ── HEADER & ON-DEMAND SCAN COMMAND BAR ── */}
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5 bg-[#0F172A]/90 backdrop-blur-xl p-6 rounded-3xl border border-white/[0.08] shadow-2xl relative overflow-hidden">
                <div className="flex items-start sm:items-center gap-4 relative z-10">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-600/20 to-purple-600/10 border border-red-500/30 flex items-center justify-center shrink-0 shadow-[0_0_25px_rgba(239,68,68,0.2)]">
                        <Bug className="w-7 h-7 text-red-500 animate-pulse" />
                    </div>
                    <div>
                        <div className="flex flex-wrap items-center gap-3">
                            <h1 className="text-2xl lg:text-3xl font-black text-slate-100 tracking-tight uppercase">
                                Dark Web &amp; Deep Web Intelligence
                            </h1>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                Live Sweeper Active
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 flex flex-wrap items-center gap-2 font-medium">
                            <Radio className="w-3.5 h-3.5 text-blue-400" />
                            Tor Onion Marketplaces &bull; Telegram Stealer Botnets &bull; Leaked Database Dumps &bull; OSINT Archives
                        </p>
                    </div>
                </div>

                {/* Right Action & On-Demand Scanner */}
                <div className="flex flex-wrap items-center gap-3 relative z-10">
                    {/* Auto-Sync Toggle */}
                    <div className="flex items-center gap-2 bg-[#1E293B]/70 px-3 py-2 rounded-xl border border-white/[0.06]">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Auto-Sync</span>
                        <button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={`w-9 h-5 rounded-full transition-colors relative flex items-center p-0.5 ${autoRefresh ? "bg-blue-600" : "bg-slate-700"}`}
                        >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${autoRefresh ? "translate-x-4" : "translate-x-0"}`} />
                        </button>
                    </div>

                    {/* Scan Form */}
                    <form onSubmit={handleManualScan} className="flex items-center bg-[#1E293B]/90 border border-white/[0.1] rounded-xl overflow-hidden focus-within:border-blue-500/60 shadow-inner">
                        <select
                            value={scanType}
                            onChange={(e) => setScanType(e.target.value)}
                            className="bg-transparent text-[11px] font-bold uppercase text-slate-300 px-3 py-2 outline-none border-r border-white/[0.08] cursor-pointer"
                        >
                            <option value="email" className="bg-[#1E293B] text-slate-200">Email</option>
                            <option value="domain" className="bg-[#1E293B] text-slate-200">Domain</option>
                        </select>
                        <div className="flex items-center px-3 py-1.5 min-w-[240px]">
                            <Search className="w-3.5 h-3.5 text-slate-400 mr-2 shrink-0" />
                            <input
                                type="text"
                                placeholder={scanType === "email" ? "Enter email (e.g. user@querytel.com)..." : "Enter domain (e.g. querytel.com)..."}
                                className="bg-transparent text-xs font-medium text-slate-100 placeholder:text-slate-500 outline-none w-full"
                                value={scanInput}
                                onChange={(e) => setScanInput(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isScanning || !scanInput.trim()}
                            className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 text-white text-xs font-black uppercase tracking-wider flex items-center gap-1.5 transition-all cursor-pointer shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                        >
                            {isScanning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                            Deep Scan
                        </button>
                    </form>

                    {/* Refresh Button */}
                    <button
                        onClick={() => { clearFilters(); fetchData(); }}
                        disabled={loading}
                        className="p-2.5 bg-[#1E293B]/80 hover:bg-[#1E293B] border border-white/[0.08] text-slate-300 hover:text-white rounded-xl text-xs transition-all active:scale-95 disabled:opacity-50"
                        title="Reset & Sync All Data"
                    >
                        <RefreshCw className={`w-4 h-4 text-blue-400 ${loading ? "animate-spin" : ""}`} />
                    </button>
                </div>
            </div>

            {/* ── SCAN AUDIT RESULT BANNER ── */}
            {scanResult && (
                <div className={`p-5 rounded-3xl border shadow-2xl relative overflow-hidden animate-in fade-in slide-in-from-top-3 duration-300 ${
                    scanResult.isClean
                        ? "bg-emerald-950/40 border-emerald-500/30 text-emerald-300 shadow-[0_0_30px_rgba(16,185,129,0.1)]"
                        : "bg-red-950/40 border-red-500/30 text-red-300 shadow-[0_0_30px_rgba(239,68,68,0.1)]"
                }`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
                        <div className="flex items-start gap-3.5">
                            <div className={`p-3 rounded-2xl border shrink-0 ${
                                scanResult.isClean
                                    ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
                                    : "bg-red-500/20 border-red-500/40 text-red-400"
                            }`}>
                                {scanResult.isClean ? <ShieldCheck className="w-6 h-6" /> : <AlertTriangle className="w-6 h-6" />}
                            </div>
                            <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-black/40 border border-white/[0.08]">
                                        Target: {scanResult.query}
                                    </span>
                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md border ${
                                        scanResult.isClean
                                            ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-300"
                                            : "bg-red-500/20 border-red-500/30 text-red-300"
                                    }`}>
                                        {scanResult.isClean ? "0 THREATS FOUND (CLEAN)" : `${scanResult.alertCount} EXPOSURES DETECTED`}
                                    </span>
                                </div>
                                <p className="text-xs font-medium text-slate-200 leading-relaxed max-w-3xl">
                                    {scanResult.message}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={clearFilters}
                                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-colors"
                            >
                                View Global Matrix
                            </button>
                            <button onClick={() => setScanResult(null)} className="p-2 text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {scanResult.isClean && (
                        <div className="mt-4 pt-3 border-t border-emerald-500/20 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono text-emerald-400/90">
                            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Tor .onion Forums: Clean</span>
                            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Telegram Stealer Logs: Clean</span>
                            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Breach Dumps: Clean</span>
                            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Pastebin &amp; Gists: Clean</span>
                        </div>
                    )}
                </div>
            )}

            {/* ── 4 EXECUTIVE KPI METRICS STRIP ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricTile
                    title="Total Monitored Exposures"
                    value={stats.totalAlerts || totalAlertsCount}
                    sub="Verified OSINT & Darknet Mentions"
                    icon={History}
                    color="blue"
                    badge="DATABASE"
                />
                <MetricTile
                    title="High & Critical Priority"
                    value={stats.highSeverity}
                    sub="Active Exploitable Vulnerabilities"
                    icon={ShieldAlert}
                    color="red"
                    badge="CRITICAL"
                />
                <MetricTile
                    title="Confirmed Data Dumps"
                    value={stats.totalBreaches}
                    sub="Credentials, Configs & Leaks"
                    icon={Lock}
                    color="orange"
                    badge="EXPOSURES"
                />
                <MetricTile
                    title="Threat Exposure Index"
                    value={stats.highSeverity > 5 ? "CRITICAL" : stats.highSeverity > 0 ? "HIGH" : "ELEVATED"}
                    sub="Correlated Risk Exposure"
                    icon={TrendingUp}
                    color={stats.highSeverity > 5 ? "red" : "purple"}
                    badge="INDEX"
                />
            </div>

            {/* ── REAL-TIME ALERTS WORKSPACE (FULL WIDTH) ── */}
            <div className="bg-[#0F172A]/90 backdrop-blur-xl rounded-3xl border border-white/[0.08] shadow-2xl overflow-hidden flex flex-col">

                    {/* Table Title Bar */}
                    <div className="p-6 border-b border-white/[0.06] flex flex-wrap items-center justify-between gap-4 bg-[#1E293B]/40">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400">
                                <Radio className="w-4 h-4" />
                            </div>
                            <div>
                                <h2 className="text-base font-black text-slate-100 uppercase tracking-tight">
                                    Real-Time Dark Web Exposures
                                </h2>
                                <p className="text-[10px] text-slate-400 font-medium">
                                    {activeSearch ? `Showing results matching "${activeSearch}"` : `Showing ${alerts.length} of ${totalAlertsCount} verified darknet intelligence events`}
                                </p>
                            </div>
                        </div>

                        {/* Export & Actions */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => exportData("csv")}
                                className="px-3 py-1.5 bg-[#1E293B] hover:bg-[#334155] border border-white/[0.08] text-slate-300 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all"
                                title="Export CSV"
                            >
                                <Download className="w-3.5 h-3.5 text-blue-400" /> CSV
                            </button>
                            <button
                                onClick={() => exportData("json")}
                                className="px-3 py-1.5 bg-[#1E293B] hover:bg-[#334155] border border-white/[0.08] text-slate-300 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all"
                                title="Export JSON"
                            >
                                <Download className="w-3.5 h-3.5 text-purple-400" /> JSON
                            </button>
                        </div>
                    </div>

                    {/* Filter Toolbar */}
                    <div className="p-4 border-b border-white/[0.06] bg-[#1E293B]/20 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2.5">
                            <div className="flex items-center gap-1.5 text-slate-400 text-xs font-bold uppercase tracking-wider pr-2 border-r border-slate-800">
                                <Filter className="w-3.5 h-3.5 text-blue-400" />
                                Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                            </div>

                            {/* Live Search */}
                            <div className="flex items-center gap-2 bg-[#1E293B]/80 border border-white/[0.06] rounded-xl px-3 py-1.5 focus-within:border-blue-500/50">
                                <Search className="w-3.5 h-3.5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Filter by keyword, title, target..."
                                    className="bg-transparent text-xs font-medium text-slate-200 outline-none w-48 placeholder:text-slate-500"
                                    value={activeSearch}
                                    onChange={(e) => { setActiveSearch(e.target.value); setPage(1); }}
                                />
                                {activeSearch && (
                                    <button onClick={() => { setActiveSearch(""); setScanResult(null); }} className="text-slate-500 hover:text-slate-300">
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* Severity Filter */}
                            <select
                                value={filters.severity}
                                onChange={(e) => { setFilters(prev => ({ ...prev, severity: e.target.value })); setPage(1); }}
                                className="bg-[#1E293B]/80 border border-white/[0.06] text-slate-200 text-xs font-bold px-3 py-1.5 rounded-xl outline-none focus:border-blue-500/50 cursor-pointer uppercase"
                            >
                                <option value="">All Severities</option>
                                <option value="high">High Severity</option>
                                <option value="medium">Medium Severity</option>
                                <option value="low">Low Severity</option>
                            </select>

                            {/* Alert Type Filter */}
                            <select
                                value={filters.type}
                                onChange={(e) => { setFilters(prev => ({ ...prev, type: e.target.value })); setPage(1); }}
                                className="bg-[#1E293B]/80 border border-white/[0.06] text-slate-200 text-xs font-bold px-3 py-1.5 rounded-xl outline-none focus:border-blue-500/50 cursor-pointer uppercase"
                            >
                                <option value="">All Types</option>
                                <option value="Data Leak">Data Leak</option>
                                <option value="Credential Dump">Credential Dump</option>
                                <option value="Exploit Chatter">Exploit Chatter</option>
                                <option value="Ransomware Exposure">Ransomware Exposure</option>
                                <option value="Config Leak">Config Leak</option>
                                <option value="Paste">Paste / OSINT</option>
                            </select>

                            {/* Status Filter */}
                            <select
                                value={filters.status}
                                onChange={(e) => { setFilters(prev => ({ ...prev, status: e.target.value })); setPage(1); }}
                                className="bg-[#1E293B]/80 border border-white/[0.06] text-slate-200 text-xs font-bold px-3 py-1.5 rounded-xl outline-none focus:border-blue-500/50 cursor-pointer uppercase"
                            >
                                <option value="">All Statuses</option>
                                <option value="new">New</option>
                                <option value="reviewed">Reviewed</option>
                                <option value="investigating">Investigating</option>
                                <option value="mitigated">Mitigated</option>
                            </select>
                        </div>

                        {activeFilterCount > 0 && (
                            <button
                                onClick={clearFilters}
                                className="text-xs font-bold text-slate-400 hover:text-slate-200 uppercase tracking-wider"
                            >
                                Reset Filters
                            </button>
                        )}
                    </div>

                    {/* Table View */}
                    <div className="flex-1 overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-[#1E293B]/60 border-b border-white/[0.06]">
                                <tr>
                                    <th className="px-5 py-3.5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Source / Type</th>
                                    <th className="px-5 py-3.5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Threat Description</th>
                                    <th className="px-5 py-3.5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Target Asset</th>
                                    <th className="px-5 py-3.5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Severity</th>
                                    <th className="px-5 py-3.5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {loading && alerts.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center justify-center gap-3">
                                                <RefreshCw className="w-7 h-7 text-blue-400 animate-spin" />
                                                <p className="text-xs font-black uppercase tracking-widest text-slate-400">
                                                    Interrogating Darknet Repositories...
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : alerts.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center justify-center max-w-md mx-auto space-y-3">
                                                <div className="p-4 bg-emerald-500/10 rounded-2xl border border-emerald-500/20 text-emerald-400">
                                                    <ShieldCheck className="w-10 h-10" />
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-black text-slate-200 uppercase tracking-wider">
                                                        {activeSearch ? `Target "${activeSearch}" is Clean` : "No exposures matching filter"}
                                                    </h4>
                                                    <p className="text-xs text-slate-400 font-medium mt-1 leading-relaxed">
                                                        {activeSearch
                                                            ? "Zero compromised credentials or data spill records found across monitored Tor .onion forums, botnet stealer logs, and breach repositories."
                                                            : "Adjust your filter criteria or reset to view all active threat intelligence events."
                                                        }
                                                    </p>
                                                </div>
                                                {activeFilterCount > 0 && (
                                                    <button
                                                        onClick={clearFilters}
                                                        className="px-4 py-2 bg-[#1E293B] hover:bg-[#334155] border border-white/[0.08] text-slate-200 text-xs font-bold uppercase rounded-xl transition-all"
                                                    >
                                                        Show All Monitored Feeds
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    alerts.map((alert) => {
                                        const sev = (alert.severity || "medium").toLowerCase();
                                        const sevStyle = SEVERITY_CONFIG[sev] || SEVERITY_CONFIG.medium;

                                        return (
                                            <tr
                                                key={alert._id}
                                                className="group hover:bg-[#1E293B]/70 transition-colors cursor-pointer"
                                                onClick={() => setInspectAlert(alert)}
                                            >
                                                {/* Source / Type */}
                                                <td className="px-5 py-4">
                                                    <div className="flex flex-col">
                                                        <span className="text-xs font-black text-slate-100 tracking-tight flex items-center gap-1.5">
                                                            <span className={`w-2 h-2 rounded-full ${sevStyle.dot}`} />
                                                            {alert.source}
                                                        </span>
                                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5 flex items-center gap-1">
                                                            {alert.type === "Credential Dump" ? <Lock className="w-2.5 h-2.5 text-amber-400" /> : <Globe2 className="w-2.5 h-2.5 text-blue-400" />}
                                                            {alert.type}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Description */}
                                                <td className="px-5 py-4 max-w-sm">
                                                    <div className="flex flex-col gap-1">
                                                        <h4 className="text-xs font-bold text-slate-200 group-hover:text-blue-300 transition-colors leading-snug line-clamp-1">
                                                            {alert.title}
                                                        </h4>
                                                        <p className="text-[11px] text-slate-400 font-medium line-clamp-2 leading-relaxed">
                                                            {alert.contentSnippet}
                                                        </p>
                                                        <span className="text-[9px] font-mono text-slate-500 mt-0.5 flex items-center gap-1">
                                                            <Clock className="w-2.5 h-2.5" /> {formatTime(alert.dateFound)}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Target Asset */}
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-1.5 bg-[#1E293B] px-2.5 py-1 rounded-lg border border-white/[0.06] w-fit">
                                                        <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                                                        <span className="text-[11px] font-mono font-bold text-slate-300 truncate max-w-[150px]">
                                                            {alert.affectedEntity || "querytel.com"}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Severity */}
                                                <td className="px-5 py-4">
                                                    <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${sevStyle.badge}`}>
                                                        {sev}
                                                    </span>
                                                </td>

                                                {/* Actions */}
                                                <td className="px-5 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center justify-end gap-2">
                                                        {alert.status === "new" ? (
                                                            <button
                                                                onClick={() => handleUpdateStatus(alert._id, "reviewed")}
                                                                className="px-2.5 py-1 bg-emerald-500/10 hover:bg-emerald-500 text-emerald-400 hover:text-white rounded-lg transition-all border border-emerald-500/20 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
                                                                title="Mark as Reviewed"
                                                            >
                                                                <Check className="w-3 h-3" /> Review
                                                            </button>
                                                        ) : (
                                                            <span className="text-[9px] font-mono font-bold uppercase text-slate-400 px-2 py-0.5 rounded bg-[#1E293B] border border-white/[0.06]">
                                                                {alert.status}
                                                            </span>
                                                        )}

                                                        <button
                                                            onClick={() => setInspectAlert(alert)}
                                                            className="p-1.5 bg-[#1E293B] hover:bg-[#334155] text-slate-300 hover:text-blue-400 rounded-lg transition-all border border-white/[0.06]"
                                                            title="Inspect Exposure"
                                                        >
                                                            <Eye className="w-3.5 h-3.5" />
                                                        </button>

                                                        {alert.url && (
                                                            <a
                                                                href={alert.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="p-1.5 bg-[#1E293B] hover:bg-[#334155] text-slate-300 hover:text-blue-400 rounded-lg transition-all border border-white/[0.06]"
                                                                title="View External Source"
                                                            >
                                                                <ExternalLink className="w-3.5 h-3.5" />
                                                            </a>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="p-4 bg-[#1E293B]/40 border-t border-white/[0.06] flex items-center justify-between">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest font-mono">
                            Page {page} of {totalPages} ({totalAlertsCount} Total Records)
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={page === 1}
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                className="p-2 bg-[#1E293B] hover:bg-[#334155] disabled:opacity-30 text-slate-300 rounded-xl transition-all border border-white/[0.06]"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="text-xs font-mono font-bold text-slate-300 px-2">
                                {page}
                            </span>
                            <button
                                disabled={page === totalPages}
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                className="p-2 bg-[#1E293B] hover:bg-[#334155] disabled:opacity-30 text-slate-300 rounded-xl transition-all border border-white/[0.06]"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

            {/* ── THREAT INSPECTION DRILLDOWN MODAL ── */}
            {inspectAlert && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-[#0F172A] border border-white/[0.12] rounded-3xl max-w-2xl w-full p-6 lg:p-8 shadow-2xl relative overflow-hidden space-y-6">

                        {/* Ambient Glow */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/10 blur-[80px] pointer-events-none" />

                        {/* Modal Header */}
                        <div className="flex items-start justify-between gap-4 border-b border-white/[0.08] pb-5 relative z-10">
                            <div>
                                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                    <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${(SEVERITY_CONFIG[(inspectAlert.severity || 'medium').toLowerCase()] || SEVERITY_CONFIG.medium).badge}`}>
                                        {inspectAlert.severity} Priority
                                    </span>
                                    <span className="px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                        {inspectAlert.source}
                                    </span>
                                    <span className="text-[10px] font-mono text-slate-400">
                                        {formatTime(inspectAlert.dateFound)}
                                    </span>
                                </div>
                                <h3 className="text-lg font-black text-slate-100 uppercase tracking-tight">
                                    {inspectAlert.title}
                                </h3>
                            </div>
                            <button
                                onClick={() => setInspectAlert(null)}
                                className="p-2 rounded-xl bg-[#1E293B] text-slate-400 hover:text-white border border-white/[0.08] transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Modal Details Grid */}
                        <div className="grid grid-cols-2 gap-4 relative z-10">
                            <div className="p-3.5 bg-[#1E293B]/60 rounded-2xl border border-white/[0.04]">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Target Entity</span>
                                <span className="text-xs font-mono font-bold text-slate-200">{inspectAlert.affectedEntity || "querytel.com"}</span>
                            </div>
                            <div className="p-3.5 bg-[#1E293B]/60 rounded-2xl border border-white/[0.04]">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Threat Type</span>
                                <span className="text-xs font-bold text-slate-200">{inspectAlert.type}</span>
                            </div>
                        </div>

                        {/* Content Snippet / Payload Box */}
                        <div className="relative z-10 space-y-2">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                                Intelligence Snippet / Infiltration Payload
                            </span>
                            <div className="p-4 bg-black/60 rounded-2xl border border-white/[0.08] text-xs font-mono text-slate-300 leading-relaxed max-h-40 overflow-y-auto">
                                {inspectAlert.contentSnippet}
                            </div>
                        </div>

                        {/* Fingerprint & External URL */}
                        <div className="space-y-2 text-[11px] font-mono text-slate-400 relative z-10">
                            {inspectAlert.fingerprint && (
                                <div className="flex items-center gap-2 truncate">
                                    <Hash className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                    <span className="truncate">Hash: {inspectAlert.fingerprint}</span>
                                </div>
                            )}
                            {inspectAlert.url && (
                                <div className="flex items-center gap-2 truncate">
                                    <ExternalLink className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                    <a href={inspectAlert.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline truncate">
                                        {inspectAlert.url}
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Modal Action Buttons */}
                        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-white/[0.08] relative z-10">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handleUpdateStatus(inspectAlert._id, "reviewed")}
                                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)] cursor-pointer"
                                >
                                    Mark Reviewed
                                </button>
                                <button
                                    onClick={() => handleUpdateStatus(inspectAlert._id, "investigating")}
                                    className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-[0_0_15px_rgba(245,158,11,0.3)] cursor-pointer"
                                >
                                    Investigate
                                </button>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        navigate(`/threat-hunt?query=${encodeURIComponent(inspectAlert.affectedEntity || inspectAlert.title)}`);
                                    }}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5 shadow-[0_0_15px_rgba(59,130,246,0.3)] cursor-pointer"
                                >
                                    Launch Threat Hunt <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
